import { FC, useEffect, PropsWithChildren, useCallback, useState } from 'react';
import { getCookie, setCookie, useScript } from '@next/shared';
import TagManager from 'react-gtm-module';
import { useLocalizationContext } from 'shared/lib/localization';
import { useLogger } from 'shared/lib/logger';
import { AnalyticsContext } from '../model/tracking-context';
import { trackEvent } from '../lib/track-event';
import { useGtmConfigQuery } from '../api/use-gtm-config-query';
import { useInitialInfo } from 'shared/api/use-initial-info';

const ENABLED = 'enabled';
const DISABLED = 'disabled';
const COOKIE_TRACKING_KEY = 'B2BCloudTracking';

export const AnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
    const [decision, setDecision] = useState(getCookie(COOKIE_TRACKING_KEY));
    const [isGtmInitialized, setIsGtmInitialized] = useState(false);
    const { language } = useLocalizationContext();
    const { logger } = useLogger();

    const isAnalyticsEnabled = decision === ENABLED;

    const { initialInfo, isFetching: isLoadingInfo } = useInitialInfo(isAnalyticsEnabled);
    const { data: gtmConfig } = useGtmConfigQuery(isAnalyticsEnabled);
    const scriptLoadingStatus = useScript(gtmConfig?.gtmHelperScriptUrl ?? null, {
        shouldPreventLoad: !isAnalyticsEnabled,
    });

    const isRuRegion = language === 'ru-ru' || initialInfo?.Country?.CountryCode === 'RU';

    useEffect(() => {
        if (!isAnalyticsEnabled || isLoadingInfo || !gtmConfig?.gtmId || isGtmInitialized) {
            return;
        }

        window.kaspersky = {
            country: isRuRegion ? 'RU' : '',
        };

        TagManager.initialize({ gtmId: gtmConfig.gtmId });
        setIsGtmInitialized(true);
    }, [isAnalyticsEnabled, gtmConfig, isLoadingInfo, isRuRegion, isGtmInitialized]);

    const disableTracking = useCallback(() => {
        setCookie(COOKIE_TRACKING_KEY, DISABLED, {
            domain: window.location.host,
            'max-age': 7776000,
            secure: true,
        });
        setDecision(DISABLED);
    }, []);

    const enableTracking = useCallback(() => {
        setCookie(COOKIE_TRACKING_KEY, ENABLED);
        setDecision(ENABLED);
    }, []);

    const sendEvent = useCallback(
        (eventInfo: string) => {
            const isNotReady =
                !isAnalyticsEnabled ||
                scriptLoadingStatus === 'loading' ||
                scriptLoadingStatus === 'idle';

            if (isNotReady) return;

            trackEvent({ logger, eventInfo });
        },
        [isAnalyticsEnabled, scriptLoadingStatus],
    );

    return (
        <AnalyticsContext.Provider
            value={{
                hasDecision: Boolean(decision),
                sendAnalytics: sendEvent,
                enableTracking,
                disableTracking,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};
